import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, HittingNtilesCharts, Dropdown, number } from 'yankees-react-utils';
import { PlayerReport, useOktaQuery, Stats } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import useConfig from '../../../utils/useConfig';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import StatTooltip from '../../../components/StatTooltip';

const Wrapper = styled.div`
`;

let abortController = null;

const mlbNtilesSections = (translate) => [
    {
        name: translate('sections.reports.hitter.overview.overall'),
        stats: [
            {
                name: translate('sections.reports.hitter.overview.hitfx-ops'),
                key: 'hitfxops',
                statistic: 'HitFXOPS',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, true),
            },
            {
                name: translate('sections.reports.hitter.overview.strikeout-rate'),
                abbr: translate('sections.reports.hitter.overview.abbr-strikeout-rate'),
                key: 'krate',
                statistic: 'KPercent',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.walk-rate'),
                abbr: translate('sections.reports.hitter.overview.abbr-walk-rate'),
                key: 'ubbrate',
                statistic: 'UBBPercent',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.avg'),
                key: 'avg',
                statistic: 'AVG',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, true),
            },
            {
                name: translate('sections.reports.hitter.overview.slg'),
                key: 'slg',
                statistic: 'SLG',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, true),
            },
            {
                name: translate('sections.reports.hitter.overview.obp'),
                key: 'obp',
                statistic: 'OBP',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, true),
            },
            {
                name: translate('sections.reports.hitter.overview.wops'),
                key: 'wops',
                statistic: 'RVOPS',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, true),
            },
        ],
    },
    {
        name: translate('sections.reports.hitter.overview.swing-decisions'),
        stats: [
            {
                name: translate('sections.reports.hitter.overview.swing-decision-score'),
                abbr: translate('sections.reports.hitter.overview.abbr-swing-decision'),
                key: 'swds',
                statistic: 'Swds',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
            {
                name: translate('sections.reports.hitter.overview.should-swing-decision'),
                abbr: translate('sections.reports.hitter.overview.abbr-should-swing-decision'),
                key: 'swdssw',
                statistic: 'SwdsSw',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
            {
                name: translate('sections.reports.hitter.overview.should-take-decision'),
                abbr: translate('sections.reports.hitter.overview.abbr-should-take-decision'),
                key: 'swdstk',
                statistic: 'SwdsTk',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
            {
                name: translate('sections.reports.hitter.overview.k-zone'),
                abbr: translate('sections.reports.hitter.overview.abbr-k-zone'),
                key: 'kzon',
                statistic: 'Kzon',
                lowText: translate('sections.reports.hitter.overview.low'),
                highText: translate('sections.reports.hitter.overview.high'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.swing-rate-in-zone'),
                abbr: translate('sections.reports.hitter.overview.abbr-swing-rate-in-zone'),
                key: 'swinz',
                statistic: 'SwInZon',
                lowText: translate('sections.reports.hitter.overview.low'),
                highText: translate('sections.reports.hitter.overview.high'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.chase-rate'),
                abbr: translate('sections.reports.hitter.overview.abbr-chase-rate'),
                key: 'ch',
                statistic: 'Ch',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.swing-rate'),
                abbr: translate('sections.reports.hitter.overview.abbr-swing-rate'),
                key: 'sw',
                statistic: 'Sw',
                lowText: translate('sections.reports.hitter.overview.low'),
                highText: translate('sections.reports.hitter.overview.high'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
        ],
    },
    {
        name: translate('sections.reports.hitter.overview.contact-quality'),
        stats: [
            {
                name: translate('sections.reports.hitter.overview.contact-quality'),
                abbr: translate('sections.reports.hitter.overview.abbr-contact-quality'),
                key: 'cq',
                statistic: 'ContactQuality',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
            {
                name: translate('sections.reports.hitter.overview.contact-results'),
                abbr: translate('sections.reports.hitter.overview.abbr-contact-results'),
                key: 'cr',
                statistic: 'ContactResults',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
            {
                name: translate('sections.reports.hitter.overview.exit-velo'),
                abbr: translate('sections.reports.hitter.overview.abbr-exit-velo'),
                key: 'hitvelo',
                statistic: 'ExitVelo',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: ' mph',
            },
            {
                name: translate('sections.reports.hitter.overview.pct-over-95'),
                abbr: translate('sections.reports.hitter.overview.abbr-pct-over-95'),
                key: 'pct95',
                statistic: 'PercentHitsAbove95mph',
                lowText: translate('sections.reports.hitter.overview.low'),
                highText: translate('sections.reports.hitter.overview.high'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.launch-angle'),
                abbr: translate('sections.reports.hitter.overview.abbr-launch-angle'),
                key: 'vertla',
                statistic: 'VertLA',
                lowText: translate('sections.reports.hitter.overview.low'),
                highText: translate('sections.reports.hitter.overview.high'),
                formatter: (v) => number(v, 0),
                units: '&deg;',
            },
            {
                name: translate('sections.reports.hitter.overview.launch-angle-95'),
                abbr: translate('sections.reports.hitter.overview.abbr-launch-angle-95'),
                key: 'vertla95',
                statistic: 'VertLA95',
                lowText: translate('sections.reports.hitter.overview.low'),
                highText: translate('sections.reports.hitter.overview.high'),
                formatter: (v) => number(v, 0),
                units: '&deg;',
            },
            {
                name: translate('sections.reports.hitter.overview.swing-miss-rate'),
                abbr: translate('sections.reports.hitter.overview.abbr-swing-miss-rate'),
                key: 'ms',
                statistic: 'Ms',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.in-play'),
                abbr: translate('sections.reports.hitter.overview.abbr-in-play'),
                key: 'inplay',
                statistic: 'InPlay',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
        ],
    },
];

const minorsNtilesSections = (translate) => [
    {
        name: translate('sections.reports.hitter.overview.overall'),
        stats: [
            {
                name: translate('sections.reports.hitter.overview.hitfx-ops'),
                abbr: translate('sections.reports.hitter.overview.abbr-hitfx-ops'),
                key: 'aahitfxops',
                statistic: 'HitFXOPS',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, true),
            },
            {
                name: translate('sections.reports.hitter.overview.strikeout-rate'),
                abbr: translate('sections.reports.hitter.overview.abbr-strikeout-rate'),
                key: 'krate',
                statistic: 'KPercent',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.walk-rate'),
                abbr: translate('sections.reports.hitter.overview.abbr-walk-rate'),
                key: 'ubbrate',
                statistic: 'UBBPercent',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.contact-quality'),
                abbr: translate('sections.reports.hitter.overview.abbr-contact-quality'),
                key: 'aacq',
                statistic: 'ContactQuality',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
        ],
    },
    {
        name: translate('sections.reports.hitter.overview.swing-decisions'),
        stats: [
            {
                name: translate('sections.reports.hitter.overview.swing-decision-score'),
                abbr: translate('sections.reports.hitter.overview.abbr-swing-decision'),
                key: 'swds',
                statistic: 'Swds',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
            {
                name: translate('sections.reports.hitter.overview.should-swing-decision'),
                abbr: translate('sections.reports.hitter.overview.abbr-should-swing-decision'),
                key: 'swdssw',
                statistic: 'SwdsSw',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
            {
                name: translate('sections.reports.hitter.overview.should-take-decision'),
                abbr: translate('sections.reports.hitter.overview.abbr-should-take-decision'),
                key: 'swdstk',
                statistic: 'SwdsTk',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 1),
            },
        ],
    },
    {
        name: translate('sections.reports.hitter.overview.contact'),
        stats: [
            {
                name: translate('sections.reports.hitter.overview.swing-miss-rate'),
                abbr: translate('sections.reports.hitter.overview.abbr-swing-miss-rate'),
                key: 'ms',
                statistic: 'Ms',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.swing-miss-in-zone'),
                abbr: translate('sections.reports.hitter.overview.abbr-swing-miss-in-zone'),
                key: 'msinz',
                statistic: 'MsInZon',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.in-play'),
                abbr: translate('sections.reports.hitter.overview.abbr-in-play'),
                key: 'inplay',
                statistic: 'InPlay',
                lowText: translate('sections.reports.hitter.overview.poor'),
                highText: translate('sections.reports.hitter.overview.great'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.pct-over-95'),
                abbr: translate('sections.reports.hitter.overview.abbr-pct-over-95'),
                key: 'pct95',
                statistic: 'PercentHitsAbove95mph',
                lowText: translate('sections.reports.hitter.overview.low'),
                highText: translate('sections.reports.hitter.overview.high'),
                formatter: (v) => number(v, 0),
                units: '%',
            },
            {
                name: translate('sections.reports.hitter.overview.launch-angle-95'),
                abbr: translate('sections.reports.hitter.overview.abbr-launch-angle-95'),
                key: 'vertla95',
                statistic: 'VertLA95',
                lowText: translate('sections.reports.hitter.overview.low'),
                highText: translate('sections.reports.hitter.overview.high'),
                formatter: (v) => number(v, 0),
                units: '&deg;',
            },
        ],
    },
];

const HitterStrengthsOverview = ({ playerid }) => {
    const { t: translate } = useTranslation();
    const { CurrentYear, UsePreviousYearData } = useConfig();
    const Player = usePlayerSelector();
    const [level, setLevel] = useState(null);
    const [levelOptions, setLevelOptions] = useState([]);

    const yearOptions = [
        { name: `${CurrentYear - 1}-${CurrentYear.toString().substr(2)}`, value: -1, startyear: CurrentYear - 1, endyear: CurrentYear },
        { name: `${CurrentYear - 2}-${CurrentYear.toString().substr(2)}`, value: -2, startyear: CurrentYear - 2, endyear: CurrentYear },
        ...([CurrentYear, CurrentYear - 1, CurrentYear - 2].map((y) => ({ name: y, value: y, startyear: y, endyear: y }))),
    ];
    const [year, setYear] = useState(yearOptions[UsePreviousYearData ? 0 : 2]);

    const throwsOptions = [
        { name: translate('common.all'), value: null },
        { name: translate('baseball.rhp'), value: 1 },
        { name: translate('baseball.lhp'), value: 2 },
    ];
    const [throws, setThrows] = useState(throwsOptions[0].value);

    const { data: battingSeasons, isFetching: loadingBattingSeasons } = useOktaQuery(
        ['battingSeasons', playerid],
        (t) => Stats.seasonBatting(t, { playerid }),
    );

    useEffect(() => {
        if (battingSeasons && battingSeasons.length) {
            // set initial level to use and build list of selectable levels
            // just levels in the year range defined above
            const levels = battingSeasons
                .filter((bs) => bs.YearID >= CurrentYear - 2 && [8, 1, 2, 3, 4, 5, 10].some((x) => x === bs.LevelID))
                .map((bs) => bs.Level);
            // throw out dupes and sort by level sortorder
            const useLevels = [...new Set(levels.map((lv) => lv.LevelID))]
                .map((levelId) => levels.find((lv) => lv.LevelID === levelId))
                .sort((a, b) => a.SortOrder - b.SortOrder)
                .map((lv) => ({
                    name: lv.Abbr,
                    value: lv.LevelID,
                }));
            // add MLB+MIN and MIN if appropriate
            const hasMIN = useLevels.some((lv) => [1, 2, 3, 4, 5, 10].some((x) => x === lv.value));
            const hasMLB = useLevels.some((lv) => lv.value === 8);
            if (hasMIN) {
                if (hasMLB) useLevels.push({ name: translate('sections.reports.hitter.level-min-mlb'), value: null });
                useLevels.push({ name: translate('sections.reports.hitter.level-min'), value: 7 });
            }
            setLevelOptions(useLevels);
            const atLevel = useLevels.find((lv) => lv.value === Player.Team.Level.LevelID);
            setLevel(atLevel ?? useLevels[0]);
        }
    }, [battingSeasons, setLevel, setLevelOptions, CurrentYear, Player.Team.Level.LevelID, translate]);

    const ntilesparams = { playerid, startyear: year.startyear, endyear: year.endyear, levelid: level?.value, throws };
    // for player stats from MLB+MIN (level value null) plot ntiles from MLB
    if (ntilesparams.levelid === null) ntilesparams.ntileslevelid = 8;
    // from MIN (level value 7) plot ntiles from player's current level
    else if (ntilesparams.levelid === 7) ntilesparams.ntileslevelid = Player.Team.Level.LevelID;

    const { data: ntilesData, isFetching: loadingNtilesData } = useOktaQuery(
        ['HitMonNtiles', ntilesparams],
        (t) => {
            if (abortController) {
                abortController.abort();
            }
            abortController = new AbortController();
            return PlayerReport.hitmonNtiles(
                t,
                ntilesparams,
                { signal: abortController.signal },
            );
        },
        { enabled: !!level },
    );

    const loading = loadingNtilesData || loadingBattingSeasons;

    return (
        <Wrapper>
            <div className="d-flex justify-content-between">
                <div>
                    <h4>{translate('sections.reports.hitter.overview-title')}</h4>
                </div>
                {!loadingBattingSeasons && (
                    <div>
                        <div className="d-flex">
                            <div style={{ lineHeight: '3em' }}>{translate('common.year')}:</div>
                            <Dropdown
                                align="end"
                                className="ms-2 me-4"
                                options={yearOptions}
                                value={year?.name ?? CurrentYear}
                                onSelect={(y) => setYear(y)}
                            />
                            <div style={{ lineHeight: '3em' }}>{translate('baseball.level_one')}:</div>
                            <Dropdown
                                align="end"
                                className="ms-2 me-4"
                                options={levelOptions}
                                value={level?.name}
                                onSelect={(lv) => setLevel(lv)}
                            />
                            <div style={{ lineHeight: '3em' }}>{translate('sections.reports.hitter.pitcher-hand')}:</div>
                            <Dropdown
                                align="end"
                                className="ms-2"
                                options={throwsOptions}
                                value={throwsOptions.find((t) => t.value === throws)?.name ?? translate('common.all')}
                                onSelect={({ value }) => setThrows(value)}
                            />
                        </div>
                    </div>
                )}
            </div>
            <ReportCard>
                {loading && <LoadingSpinner />}
                {!loading && !(ntilesData?.player[0] && ntilesData?.percentiles[0]) && (
                    <h5>{ntilesparams?.levelid ? translate('sections.reports.hitter.no-data-level') : translate('sections.reports.hitter.no-mlb-data')}</h5>
                )}
                {!loading && (ntilesData?.player[0] && ntilesData?.percentiles[0]) && (
                    <HittingNtilesCharts
                        data={ntilesData}
                        sections={level?.value === 8 || level?.value === null ? mlbNtilesSections(translate) : minorsNtilesSections(translate)}
                        loading={loading}
                        statTooltip={StatTooltip}
                    />
                )}
            </ReportCard>
        </Wrapper>
    );
};

HitterStrengthsOverview.propTypes = {
    playerid: PropTypes.number.isRequired,
};

export default HitterStrengthsOverview;
